import { useEffect, useState } from "react";
import { Plus, Search, Edit2, QrCode, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DeviceForm from "./DeviceForm";
import { ClassBadge, DeviceDetail } from "./viz";
import {
  apiErrorMessage,
  inputCls,
  typeLabel,
  useContextClientId,
  useHierarchy,
} from "./hierarchy";

const SC = {
  INTACTA: "bg-green-500",
  CONSUMIDA: "bg-red-500",
  PARCIALMENTE_CONSUMIDA: "bg-amber-500",
  EXTRAVIADA: "bg-purple-500",
  MOFADA: "bg-orange-500",
  DERRETIDA: "bg-blue-500",
  DANIFICADA: "bg-rose-500",
  SUBSTITUIDA: "bg-slate-400",
};

const TYPE_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "porta_isca", label: "Porta-isca" },
  { value: "trex", label: "T-REX" },
  { value: "armadilha_luminosa", label: "Armadilha luminosa" },
  { value: "armadilha_biologica", label: "Armadilha biológica" },
  { value: "armadilha_mecanica", label: "Armadilha mecânica" },
  { value: "placa_cola", label: "Placa de cola" },
  { value: "catrex", label: "Catrex" },
  { value: "outro", label: "Outro" },
];

const STATUS_FILTERS = [
  { value: "", label: "Todos os status" },
  { value: "SEM_MONITORAMENTO", label: "Sem monitoramento" },
  { value: "INTACTA", label: "Intacta" },
  { value: "PARCIALMENTE_CONSUMIDA", label: "Parcialmente consumida" },
  { value: "CONSUMIDA", label: "Consumida" },
  { value: "MOFADA", label: "Mofada/Deteriorada" },
  { value: "EXTRAVIADA", label: "Extraviada" },
  { value: "DANIFICADA", label: "Danificada" },
  { value: "NO_CAPTURE", label: "Sem captura" },
  { value: "CAPTURE", label: "Com captura" },
];

export default function DeviceList({ onOpenPending }) {
  const contextClientId = useContextClientId();
  // Mesmas ações que /device-monitoring/devices exige (monitoramento_dispositivos.<ação>).
  const { can } = useAuth();
  const canCreate = can("monitoramento_dispositivos", "create");
  const canEdit = can("monitoramento_dispositivos", "edit");
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [typeKey, setTypeKey] = useState("");
  const [clientId, setClientId] = useState(contextClientId);
  const [unitId, setUnitId] = useState("");
  const [areaId, setAreaId] = useState("");
  const [status, setStatus] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pending, setPending] = useState(0);
  const [detail, setDetail] = useState(null);
  const { clients, units, areas } = useHierarchy({ clientId, unitId });

  // Contexto do header manda: cliente específico => escopo daquele cliente.
  useEffect(() => {
    setClientId(contextClientId);
    setUnitId("");
    setAreaId("");
  }, [contextClientId]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q, limit: 1000 };
      if (typeKey) params.type_key = typeKey;
      if (clientId) params.client_id = clientId;
      if (unitId) params.unit_id = unitId;
      if (areaId) params.area_id = areaId;
      if (status) params.status = status;
      if (includeInactive) params.include_inactive = "true";
      const r = await api.get("/device-monitoring/devices", { params });
      setRows(r.data.items || []);
      setTotal(r.data.total ?? (r.data.items || []).length);
    } catch (err) {
      setRows([]);
      setTotal(0);
      toast.error(apiErrorMessage(err, "Erro ao carregar dispositivos."));
    } finally {
      setLoading(false);
    }
    api
      .get("/device-monitoring/integrity", {
        params: clientId ? { client_id: clientId } : {},
      })
      .then(({ data }) => setPending(data.pending_link || 0))
      .catch((err) => {
        setPending(0);
        toast.error(apiErrorMessage(err, "Erro ao verificar vínculos pendentes."));
      });
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey, includeInactive, clientId, unitId, areaId, status]);

  const showClientColumn = !clientId;
  const clientName = clients.find((c) => c.id === clientId)?.name;

  const search = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <form onSubmit={search} className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar dispositivo..."
              className="w-full pl-10 pr-4 py-2 rounded-md border border-border bg-background text-sm"
            />
          </div>
          <select
            value={typeKey}
            onChange={(e) => setTypeKey(e.target.value)}
            className="px-3 py-2 rounded-md border border-border bg-background text-sm"
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
          >
            Buscar
          </button>
        </form>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4"
            />
            Inativos
          </label>
          {canCreate && (
            <button
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium"
              data-testid="device-new-btn"
            >
              <Plus className="h-4 w-4" />
              Novo Dispositivo
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <select
          data-testid="devices-filter-client"
          value={clientId}
          onChange={(e) => {
            setClientId(e.target.value);
            setUnitId("");
            setAreaId("");
          }}
          className={inputCls}
        >
          <option value="">Todos os clientes</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          data-testid="devices-filter-unit"
          value={unitId}
          disabled={!clientId}
          onChange={(e) => {
            setUnitId(e.target.value);
            setAreaId("");
          }}
          className={inputCls}
        >
          <option value="">
            {clientId ? "Todas as unidades" : "Selecione um cliente"}
          </option>
          {units.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <select
          data-testid="devices-filter-area"
          value={areaId}
          disabled={!unitId}
          onChange={(e) => setAreaId(e.target.value)}
          className={inputCls}
        >
          <option value="">
            {unitId ? "Todas as áreas" : "Selecione uma unidade"}
          </option>
          {areas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
        <select
          data-testid="devices-filter-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={inputCls}
        >
          {STATUS_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {pending > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4" />
          <span>
            {pending} dispositivo(s) pendente(s) de vínculo cliente/unidade —
            fora dos cronogramas e indicadores oficiais.
          </span>
          {onOpenPending && (
            <button
              onClick={onOpenPending}
              className="ml-auto underline font-medium"
            >
              Resolver vínculos
            </button>
          )}
        </div>
      )}

      {showForm && (
        <DeviceForm
          defaultClientId={clientId}
          device={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditing(null);
            load();
          }}
        />
      )}

      {!loading && (
        <p className="text-xs text-muted-foreground">
          {total} dispositivo(s){rows.length < total ? ` — exibindo ${rows.length}` : ""}
        </p>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/50 text-muted-foreground">
                  <th className="text-left py-3 px-4">Código</th>
                  <th className="text-left py-3 px-4">QR</th>
                  <th className="text-left py-3 px-4">Tipo</th>
                  {showClientColumn && (
                    <th className="text-left py-3 px-4">Cliente</th>
                  )}
                  <th className="text-left py-3 px-4">Unidade</th>
                  <th className="text-left py-3 px-4">Área</th>
                  <th className="text-left py-3 px-4">Subárea</th>
                  <th className="text-left py-3 px-4">Local</th>
                  <th className="text-left py-3 px-4">Periodicidade</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Classificação</th>
                  <th className="text-left py-3 px-4">Último monitoramento</th>
                  <th className="text-right py-3 px-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  return (
                    <tr key={d.id} className="border-b border-border/50">
                      <td className="py-3 px-4 font-medium">
                        <button className="underline font-mono" onClick={() => setDetail(d.id)}>
                          {d.code}
                        </button>
                        {!d.active && (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (inativo)
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {d.qr_code ? (
                          <span className="inline-flex items-center gap-1 font-mono text-xs text-muted-foreground">
                            <QrCode className="h-3 w-3" />
                            {d.qr_code}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">{typeLabel(d.type_key)}</td>
                      {showClientColumn && (
                        <td className="py-3 px-4">{d.client_name || "—"}</td>
                      )}
                      <td className="py-3 px-4">{d.unit_name || "—"}</td>
                      <td className="py-3 px-4">{d.area_name || "—"}</td>
                      <td className="py-3 px-4">{d.subarea_name || "—"}</td>
                      <td className="py-3 px-4">{d.location || "—"}</td>
                      <td className="py-3 px-4">
                        {d.periodicity ? d.periodicity.replace(/_/g, " ") : "—"}
                      </td>
                      <td className="py-3 px-4">
                        {d.last_status ? (
                          <span className="flex items-center gap-2">
                            <span
                              className={`h-2 w-2 rounded-full ${
                                SC[d.last_status] || "bg-gray-400"
                              }`}
                            />
                            {d.last_status.replace(/_/g, " ")}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <ClassBadge
                          cls={d.last_classification?.class}
                          label={d.last_classification?.label}
                          reason={d.last_classification?.reason}
                        />
                      </td>
                      <td className="py-3 px-4 whitespace-nowrap">
                        {d.last_monitored_at
                          ? new Date(d.last_monitored_at).toLocaleDateString("pt-BR")
                          : "—"}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {canEdit && (
                          <button
                            onClick={() => {
                              setEditing(d);
                              setShowForm(true);
                            }}
                            className="p-1 text-muted-foreground hover:text-foreground"
                            data-testid={`device-edit-btn-${d.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr>
                    <td
                      colSpan={14}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {clientId && !unitId && !areaId && !q && !typeKey && !status
                        ? `${clientName || "Este cliente"} ainda não possui dispositivos cadastrados.`
                        : "Nenhum dispositivo encontrado para os filtros selecionados."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {detail && <DeviceDetail deviceId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}
